import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { OfflineProvider, useOffline } from "@/contexts/OfflineContext";
import { setupAxiosInterceptors, registerLimitExceededHandler } from "@/lib/axiosInterceptor";
import UpgradePlanModal from "@/components/UpgradePlanModal";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import PoliticaPrivacidad from "@/pages/PoliticaPrivacidad";
import PoliticaCookies from "@/pages/PoliticaCookies";
import Dashboard from "@/pages/Dashboard";
import NewRental from "@/pages/NewRental";
import Returns from "@/pages/Returns";
import Customers from "@/pages/Customers";
import Inventory from "@/pages/Inventory";
import Tariffs from "@/pages/Tariffs";
import Reports from "@/pages/Reports";
import Maintenance from "@/pages/Maintenance";
import CashRegister from "@/pages/CashRegister";
import OnlineBookings from "@/pages/OnlineBookings";
import Integrations from "@/pages/Integrations";
import ActiveRentals from "@/pages/ActiveRentals";
import Providers from "@/pages/Providers";
import Settings from "@/pages/Settings";
import StoreManagement from "@/pages/StoreManagement";
import SystemHealth from "@/pages/SystemHealth";
import PlansManagement from "@/pages/PlansManagement";
import StoreSettings from "@/pages/StoreSettings";
import Help from "@/pages/Help";
import GettingStarted from "@/pages/GettingStarted";
import TrialExpired from "@/pages/TrialExpired";
import Support from "@/pages/Support";
import HelpAdmin from "@/pages/HelpAdmin";
import TeamManagement from "@/pages/TeamManagement";
import PlanSelection from "@/pages/PlanSelection";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Billing from "@/pages/Billing";
import MyAccount from "@/pages/MyAccount";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";

// ============================================================
// SERVICE WORKER REGISTRATION CON AUTO-RELOAD
// ============================================================
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('[App] Service Worker no soportado');
    return;
  }

  try {
    // Registrar el Service Worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Nunca usar caché del navegador para sw.js
    });
    
    console.log('[App] Service Worker registrado:', registration.scope);
    
    // Forzar verificación de actualizaciones
    registration.update();
    
    // Verificar actualizaciones periódicamente (cada 30 segundos en desarrollo)
    setInterval(() => {
      registration.update();
    }, 30000);

    // Escuchar cuando hay un nuevo Service Worker instalado
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('[App] Nueva versión del SW detectada, estado:', newWorker?.state);
      
      if (!newWorker) return;
      
      newWorker.addEventListener('statechange', () => {
        console.log('[App] SW nuevo estado:', newWorker.state);
        
        // Cuando el nuevo SW está instalado y hay un SW controlando
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('[App] Nueva versión lista, recargando...');
          
          // Notificar al usuario y recargar
          toast.info('Nueva versión disponible. Actualizando...', {
            duration: 2000
          });
          
          // Forzar al nuevo SW a tomar control
          newWorker.postMessage('skipWaiting');
          
          // Recargar la página después de un breve delay
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      });
    });

    // Escuchar mensajes del Service Worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('[App] Mensaje del SW:', event.data);
      
      if (event.data.type === 'SW_UPDATED') {
        console.log('[App] SW actualizado a versión:', event.data.version);
        // El SW ya se activó, recargar para usar la nueva versión
        window.location.reload();
      }
      
      if (event.data.type === 'CACHE_CLEARED') {
        console.log('[App] Caché limpiada');
        toast.success('Caché limpiada');
      }
    });

    // Si ya hay un SW esperando, activarlo
    if (registration.waiting) {
      console.log('[App] SW esperando, activando...');
      registration.waiting.postMessage('skipWaiting');
    }

  } catch (error) {
    console.error('[App] Error registrando Service Worker:', error);
  }
};

// Public Route - Redirect to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Smart root: shows LandingPage for unauthenticated users, Layout for authenticated
const SmartRoot = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Cargando...</div>
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return <Layout />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Cargando...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // ── Trial lockdown ────────────────────────────────────────────────────
  // A store whose trial expired without paying can log in but can only
  // reach the plan-selection / payment flow. Everything else bounces to
  // the full-screen "choose a plan" page. Super admin and impersonation
  // are never locked.
  const LOCKED_ALLOWED = ["/seleccionar-plan", "/pago-exitoso", "/prueba-caducada"];
  if (
    user.store_locked &&
    !user.is_impersonating &&
    user.role !== "super_admin" &&
    !LOCKED_ALLOWED.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/prueba-caducada" replace />;
  }

  // ── Role guard ────────────────────────────────────────────────────────
  // When `allowedRoles` is defined, restrict access based on user.role.
  // Falls back to a soft redirect to `/` with a toast — matches the
  // in-app UX pattern rather than a hard 403 page.
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Fire toast lazily so it appears AFTER the navigation completes,
    // otherwise the redirected page unmounts it immediately.
    setTimeout(() => {
      toast.error("No tienes permiso para acceder a esta sección.");
    }, 0);
    return <Navigate to="/" replace />;
  }

  return children;
};

// Componente que descarga datos iniciales después del login
const DataInitializer = ({ children }) => {
  const { user } = useAuth();
  const { downloadInitialData, isInitialized } = useOffline();
  
  useEffect(() => {
    if (user && isInitialized) {
      // Descargar datos en segundo plano después del login
      downloadInitialData();
    }
  }, [user, isInitialized, downloadInitialData]);
  
  return children;
};

function AppRoutes() {
  return (
    <DataInitializer>
      <Routes>
        {/* Public Routes */}
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/prueba-caducada" element={<TrialExpired />} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        {/* Legal pages - always public */}
        <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/politica-cookies" element={<PoliticaCookies />} />

        {/* Smart root: LandingPage (no auth) or Dashboard (auth) */}
        <Route path="/" element={<SmartRoot />}>
          <Route index element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="nuevo-alquiler" element={<ProtectedRoute><NewRental /></ProtectedRoute>} />
          <Route path="alquileres-activos" element={<ProtectedRoute><ActiveRentals /></ProtectedRoute>} />
          <Route path="devoluciones" element={<ProtectedRoute><Returns /></ProtectedRoute>} />
          <Route path="clientes" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
          <Route path="proveedores" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Providers /></ProtectedRoute>} />
          <Route path="inventario" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
          <Route path="tarifas" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Tariffs /></ProtectedRoute>} />
          <Route path="reportes" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Reports /></ProtectedRoute>} />
          <Route path="mantenimiento" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
          <Route path="caja" element={<ProtectedRoute><CashRegister /></ProtectedRoute>} />
          <Route path="empieza-aqui" element={<ProtectedRoute><GettingStarted /></ProtectedRoute>} />
          <Route path="integraciones" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Integrations /></ProtectedRoute>} />
          <Route path="reservas-online" element={<ProtectedRoute><OnlineBookings /></ProtectedRoute>} />
          <Route path="configuracion" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Settings /></ProtectedRoute>} />
          <Route path="tiendas" element={<ProtectedRoute allowedRoles={["super_admin"]}><StoreManagement /></ProtectedRoute>} />
          <Route path="salud-sistema" element={<ProtectedRoute allowedRoles={["super_admin"]}><SystemHealth /></ProtectedRoute>} />
          <Route path="planes" element={<ProtectedRoute allowedRoles={["super_admin"]}><PlansManagement /></ProtectedRoute>} />
          <Route path="tiendas/:storeId/ajustes" element={<ProtectedRoute allowedRoles={["super_admin"]}><StoreSettings /></ProtectedRoute>} />
          <Route path="ayuda" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="ayuda/admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><HelpAdmin /></ProtectedRoute>} />
          <Route path="equipo" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><TeamManagement /></ProtectedRoute>} />
          <Route path="soporte" element={<ProtectedRoute><Support /></ProtectedRoute>} />
          <Route path="seleccionar-plan" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><PlanSelection /></ProtectedRoute>} />
          <Route path="pago-exitoso" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><PaymentSuccess /></ProtectedRoute>} />
          <Route path="facturacion" element={<ProtectedRoute allowedRoles={["admin","super_admin"]}><Billing /></ProtectedRoute>} />
          <Route path="mi-cuenta" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
        </Route>
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DataInitializer>
  );
}

function App() {
  const [limitModalData, setLimitModalData] = useState(null);

  // Configurar interceptor de Axios y registrar manejador de límites
  useEffect(() => {
    setupAxiosInterceptors();
    
    registerLimitExceededHandler((data) => {
      setLimitModalData({
        isOpen: true,
        limitType: data.limitType,
        currentCount: data.currentCount || 0,
        maxAllowed: data.maxAllowed || 0,
        planName: data.planName || "Plan Actual"
      });
    });
  }, []);

  // Registrar Service Worker al montar
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <OfflineProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors closeButton />
              
              {/* Modal Global de Límites de Plan */}
              {limitModalData && (
                <UpgradePlanModal
                  isOpen={limitModalData.isOpen}
                  onClose={() => setLimitModalData(null)}
                  limitType={limitModalData.limitType}
                  currentCount={limitModalData.currentCount}
                  maxAllowed={limitModalData.maxAllowed}
                  planName={limitModalData.planName}
                />
              )}
            </OfflineProvider>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
